import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { CheckCircle, Send, MapPin, MessageSquare, Cpu, Mail, Clock } from 'lucide-react';
import Seo from './mkt/ui/Seo';

const INQUIRY_TYPES = [
  { value: 'sales', label: 'Sales Inquiry' },
  { value: 'support', label: 'Technical Support' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'billing', label: 'Billing Question' },
  { value: 'general', label: 'General Question' },
  { value: 'demo', label: 'Request Demo' },
  { value: 'something_else', label: 'Something Else…' },
];

const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const validatePhone = (p) => /^\d{10}$/.test(p.replace(/\D/g, ''));

function Contact() {
  const [form, setForm] = useState({
    firstName: '', lastName: '', company: '', email: '', phone: '',
    inquiryType: INQUIRY_TYPES[0], subject: '', message: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const recaptchaRef = useRef();
  const navigate = useNavigate();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSelectChange = (name, value) => setForm({ ...form, [name]: value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!termsAccepted) { setError('Please accept the Terms and Privacy Policy to continue.'); setLoading(false); return; }
    if (!form.firstName || !form.lastName || !form.company || !form.email || !form.phone ||
        !form.inquiryType || !form.subject || !form.message) {
      setError('All fields are required.'); setLoading(false); return;
    }
    if (!validateEmail(form.email)) { setError('Invalid email address.'); setLoading(false); return; }
    if (!validatePhone(form.phone)) { setError('Invalid phone number. Enter 10 digits.'); setLoading(false); return; }
    if (!captchaToken) { setError('Please complete the CAPTCHA.'); setLoading(false); return; }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: `${form.firstName} ${form.lastName}`,
          company_name: form.company,
          email: form.email,
          phone: form.phone,
          inquiry_type: form.inquiryType.value,
          subject: form.subject,
          message: form.message,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Failed to send message. Please try again.'); setLoading(false); return; }
      setSuccess(true);
      setLoading(false);
    } catch (err) {
      setError('Failed to send message. Please try again.');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section className="cp-hero">
        <div className="cp-container" style={{ maxWidth: 540, textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 72, height: 72, borderRadius: '50%',
            background: 'rgba(16, 185, 129, 0.12)', color: '#047857',
            marginBottom: 24,
          }}>
            <CheckCircle size={36} />
          </div>
          <h1 className="cp-h2" style={{ fontSize: 'var(--fs-h1)' }}>Message sent.</h1>
          <p className="cp-lede" style={{ margin: '12px auto 32px' }}>
            Thanks for reaching out. We'll get back to you within one business day.
          </p>
          <button className="cp-btn cp-btn--primary cp-btn--lg" onClick={() => navigate('/')}>
            Back home
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="cp-hero" style={{ paddingBottom: 96 }}>
      <Seo title="Contact" path="/contact" description="Tell us about your operation — we'll come back with a tailored walkthrough or same-day demo." />
      <div className="cp-container cp-hero-grid" style={{ alignItems: 'start' }}>
        {/* Left: sales copy */}
        <div>
          <span className="cp-eyebrow">Contact us</span>
          <h1 className="cp-h1" style={{ marginTop: 20 }}>Let's talk.</h1>
          <p className="cp-lede" style={{ marginBottom: 32 }}>
            Tell us what you operate, what already reports, and what your teams do when
            field. We'll come back with a tailored walkthrough or a same-day demo.
          </p>

          <div style={{ display: 'grid', gap: 14, marginBottom: 32 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', color: 'var(--ink-500)' }}>
              <Mail size={16} color="var(--brand-500)" />
              <a href="mailto:admin@coplaid.ai" style={{ color: 'var(--ink-700)', fontWeight: 500 }}>
                admin@coplaid.ai
              </a>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', color: 'var(--ink-500)' }}>
              <Clock size={16} color="var(--brand-500)" />
              <span>Replies within one business day · Mon–Fri</span>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 12 }}>
            <div className="cp-card" style={{ padding: 20, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: 'var(--brand-50)', color: 'var(--brand-600)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <MapPin size={18} />
              </div>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--ink-700)', marginBottom: 2 }}>The platform</div>
                <div style={{ color: 'var(--ink-500)', fontSize: 14 }}>
                  Replan the day in minutes, not hours.
                </div>
              </div>
            </div>
            <div className="cp-card" style={{ padding: 20, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: 'var(--brand-50)', color: 'var(--brand-600)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <MessageSquare size={18} />
              </div>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--ink-700)', marginBottom: 2 }}>Communication</div>
                <div style={{ color: 'var(--ink-500)', fontSize: 14 }}>
                  Reach the right person on a channel they already use, and hear back.
                </div>
              </div>
            </div>
            <div className="cp-card" style={{ padding: 20, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: 'var(--brand-50)', color: 'var(--brand-600)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Cpu size={18} />
              </div>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--ink-700)', marginBottom: 2 }}>
                  Embedded Intelligence
                </div>
                <div style={{ color: 'var(--ink-500)', fontSize: 14 }}>
                  Connected devices and edge automation.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: form card */}
        <div className="cp-card" style={{ padding: 32 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink-700)', margin: '0 0 8px' }}>Send a message</h2>
          <p style={{ color: 'var(--ink-500)', fontSize: 14, margin: '0 0 24px' }}>
            All fields required.
          </p>

          {error && (
            <div style={{
              padding: '10px 14px',
              background: 'rgba(220, 38, 38, 0.08)',
              border: '1px solid rgba(220, 38, 38, 0.2)',
              color: '#b91c1c',
              borderRadius: 'var(--radius-sm)',
              fontSize: 14, marginBottom: 16,
            }}>{error}</div>
          )}

          <form onSubmit={handleSubmit} className="cp-form">
            <div className="cp-form-grid">
              <div className="cp-form-field">
                <label className="cp-form-label">First name</label>
                <input className="cp-form-input" type="text" name="firstName" value={form.firstName} onChange={handleChange} required />
              </div>
              <div className="cp-form-field">
                <label className="cp-form-label">Last name</label>
                <input className="cp-form-input" type="text" name="lastName" value={form.lastName} onChange={handleChange} required />
              </div>
              <div className="cp-form-field cp-form-field--full">
                <label className="cp-form-label">Company</label>
                <input className="cp-form-input" type="text" name="company" value={form.company} onChange={handleChange} required />
              </div>
              <div className="cp-form-field">
                <label className="cp-form-label">Email</label>
                <input className="cp-form-input" type="email" name="email" value={form.email} onChange={handleChange} required />
              </div>
              <div className="cp-form-field">
                <label className="cp-form-label">Phone</label>
                <input className="cp-form-input" type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="10 digits" required />
              </div>
              <div className="cp-form-field cp-form-field--full">
                <label className="cp-form-label">Inquiry type</label>
                <select
                  className="cp-form-input"
                  name="inquiryType"
                  value={form.inquiryType.value}
                  onChange={(e) => {
                    const selected = INQUIRY_TYPES.find((t) => t.value === e.target.value);
                    handleSelectChange('inquiryType', selected);
                  }}
                  required
                >
                  {INQUIRY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="cp-form-field cp-form-field--full">
                <label className="cp-form-label">Subject</label>
                <input className="cp-form-input" type="text" name="subject" value={form.subject} onChange={handleChange} placeholder="Brief subject line" required />
              </div>
              <div className="cp-form-field cp-form-field--full">
                <label className="cp-form-label">Message</label>
                <textarea className="cp-form-input cp-form-textarea" name="message" value={form.message} onChange={handleChange} placeholder="Tell us about your inquiry…" rows={4} required />
              </div>
              <div className="cp-form-field cp-form-field--full" style={{ display: 'flex', justifyContent: 'center' }}>
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  size="normal"
                  ref={recaptchaRef}
                  onChange={(token) => setCaptchaToken(token)}
                />
              </div>
            </div>

            <label style={{
              display: 'flex', gap: 10, alignItems: 'flex-start',
              padding: '12px 14px',
              background: 'var(--surface-1)',
              border: '1px solid var(--ink-100)',
              borderRadius: 'var(--radius-sm)',
              margin: '16px 0',
              cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                style={{ marginTop: 3, accentColor: 'var(--brand-500)' }}
              />
              <span style={{ fontSize: 13, color: 'var(--ink-500)', lineHeight: 1.5 }}>
                By submitting, you agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-600)' }}>Terms</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-600)' }}>Privacy Policy</a>.
              </span>
            </label>

            <button
              type="submit"
              className="cp-btn cp-btn--primary cp-btn--lg"
              style={{ width: '100%' }}
              disabled={loading}
            >
              <Send size={16} /> {loading ? 'Sending…' : 'Send message'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

export default Contact;
